export type AppTheme = 'light' | 'dark';

type ThemeChangeListener = (theme: AppTheme) => void;

const STORAGE_KEY = 'paint_meister_theme';
const DEFAULT_THEME: AppTheme = 'dark';

class ThemeManager {
  private currentTheme: AppTheme = DEFAULT_THEME;
  private readonly listeners = new Set<ThemeChangeListener>();

  constructor() {
    const savedTheme = localStorage.getItem(STORAGE_KEY);

    if (savedTheme === 'light' || savedTheme === 'dark') {
      this.currentTheme = savedTheme;
    }

    this.applyTheme();
  }

  public getTheme(): AppTheme {
    return this.currentTheme;
  }

  public setTheme(theme: AppTheme): void {
    const hasChanged = theme !== this.currentTheme;

    this.currentTheme = theme;
    localStorage.setItem(STORAGE_KEY, theme);
    this.applyTheme();

    if (hasChanged) {
      this.notifyListeners();
    }
  }

  public toggleTheme(): AppTheme {
    const nextTheme: AppTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(nextTheme);
    return nextTheme;
  }

  public subscribe(cb: ThemeChangeListener): () => void {
    this.listeners.add(cb);

    return () => {
      this.listeners.delete(cb);
    };
  }

  private applyTheme(): void {
    document.body.setAttribute('data-theme', this.currentTheme);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.currentTheme));
  }
}

export const themeManager = new ThemeManager();
